import React from 'react';

import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useGetMonthlyPaymentStatistic } from 'src/features/statistic/api/useGetMonthlyPaymentStatistic';
import MonthPicker from 'src/features/statistic/components/MonthPicker';
import WidgetDurationBreakdown from 'src/features/statistic/components/WidgetDurationBreakdown';
import { DataKey, TransformedBarItem } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import 'dayjs/locale/bg';
import { CURRENCY } from 'src/types';
import { useParking } from 'src/providers/ParkingProvider';
import { colorMonthlyPaymentMapping, statisticsTypeMapping } from 'src/features/statistic/utils/constants';
import { safeFormatMessage } from 'src/utils/intl';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

type PaymentData = {
    day: number;
    [key: string]: number;
}[];

export default function MonthlyPaymentStatistics() {
    const currency = CURRENCY.EUR;
    const { formatMessage, formatNumber, locale } = useIntl();
    dayjs.locale(locale);

    const { parkingId } = useParking();
    const { setTitle, removeTitle } = useTitleStore();
    const [selectedDate, setSelectedDate] = React.useState(dayjs());

    const accessPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const paymentsRoute = `/${PATH.PAYMENTS}/payments`;

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.monthlyPaymentBreakdown));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const fromDatetime = selectedDate.set('date', 1).set('hour', 0).set('minute', 0).format(DATE_FORMAT);
    const toDatetime = selectedDate
        .add(1, 'month')
        .set('date', 1)
        .subtract(1, 'day')
        .set('hour', 23)
        .set('minute', 59)
        .format(DATE_FORMAT);

    const { data, isLoading } = useGetMonthlyPaymentStatistic({
        from_datetime: fromDatetime,
        to_datetime: toDatetime,
        parkingId,
    });

    const params = new URLSearchParams();
    params.set(
        'filters',
        JSON.stringify([
            {
                id: 'date',
                value: {
                    afterDate: fromDatetime,
                    beforeDate: toDatetime,
                },
            },
        ]),
    );

    const paymentData = React.useMemo(() => {
        const total = data?.totals
            ? formatNumber(data.totals.total_amount / 100, {
                  style: 'currency',
                  currency,
              })
            : '-';

        const average = data?.totals
            ? formatNumber(data.totals.average_amount / 100, {
                  style: 'currency',
                  currency,
              })
            : '-';

        return [
            { title: formatMessage(statisticMessages.total), value: total },
            { title: formatMessage(statisticMessages.transactions), value: data?.totals.transactions_count ?? '-' },
            { title: formatMessage(statisticMessages.average), value: average },
            { title: formatMessage(statisticMessages.methods), value: data?.totals.methods_count ?? '-' },
            { title: formatMessage(statisticMessages.products), value: data?.totals.products_count ?? '-' },
            {
                title: formatMessage(statisticMessages.paymentMachines),
                value: data?.totals.payment_machines_count ?? '-',
            },
        ];
    }, [currency, data?.totals, formatMessage, formatNumber]);

    const transformPaymentData = React.useCallback(
        ({
            data,
            translate,
        }: {
            data?: PaymentData;
            translate?: boolean;
        }): {
            dataKeys: DataKey[];
            data: TransformedBarItem[];
        } => {
            if (!data)
                return {
                    dataKeys: [],
                    data: [],
                };

            const dataKeys: DataKey[] = Array.from(
                data.reduce((acc, currentValue) => {
                    const { day, ...rest } = currentValue;
                    Object.keys(rest).map((key) => acc.add(key));

                    return acc;
                }, new Set<string>()),
            ).map((key) => ({
                title: translate ? safeFormatMessage(formatMessage, key.replaceAll('_', '-'), key) : key,
                value: key,
            }));

            const newData: TransformedBarItem[] = data.map((item) => ({
                ...item,
                title: dayjs(selectedDate).set('date', item.day).format('D MMM'),
            }));

            return {
                dataKeys,
                data: newData,
            };
        },
        [formatMessage, selectedDate],
    );

    const paymentMachineKeys = [
        ...new Set(
            data?.payments_by_payment_machine?.flatMap((item) => Object.keys(item).filter((key) => key !== 'day')),
        ),
    ];

    const paymentProductKeys = [
        ...new Set(data?.payments_by_product?.flatMap((item) => Object.keys(item).filter((key) => key !== 'day'))),
    ];

    return (
        <>
            <div className="flex flex-1 flex-col gap-6">
                <div className="flex justify-end">
                    <MonthPicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
                </div>
                <GeneralBlock className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                    {paymentData.map(({ title, value }, i) => (
                        <div key={i} className="flex flex-col">
                            <span className="text-sm text-gray-600">{title}</span>
                            {isLoading ? (
                                <Skeleton className="h-8 w-24" />
                            ) : (
                                <span className="text-xl font-semibold sm:text-2xl">{value}</span>
                            )}
                        </div>
                    ))}
                </GeneralBlock>
                <div className="flex flex-col gap-6">
                    <WidgetDurationBreakdown
                        title={formatMessage(statisticMessages.paymentsByMethod)}
                        isLoading={isLoading}
                        colorMapping={colorMonthlyPaymentMapping}
                        {...transformPaymentData({ data: data?.payments_by_method, translate: true })}
                        filterParams={params}
                        viewAllRoute={accessPayments ? paymentsRoute : undefined}
                    />
                    <WidgetDurationBreakdown
                        title={formatMessage(statisticMessages.paymentsByMachine)}
                        isLoading={isLoading}
                        dynamicStats={paymentMachineKeys}
                        getLabel={(entry) => entry}
                        {...transformPaymentData({ data: data?.payments_by_payment_machine })}
                        filterParams={params}
                        viewAllRoute={accessPayments ? paymentsRoute : undefined}
                    />
                    <WidgetDurationBreakdown
                        title={formatMessage(statisticMessages.paymentsByProduct)}
                        isLoading={isLoading}
                        dynamicStats={paymentProductKeys}
                        getLabel={(entry) => {
                            return formatMessage(
                                statisticsTypeMapping[entry] ?? {
                                    id: 'common.unknown',
                                    defaultMessage: entry,
                                },
                            );
                        }}
                        {...transformPaymentData({ data: data?.payments_by_product })}
                        filterParams={params}
                        viewAllRoute={accessPayments ? paymentsRoute : undefined}
                    />
                </div>
            </div>
        </>
    );
}
